import { Component, DestroyRef, EventEmitter, Input, OnInit, Output, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { InputNumberModule } from 'primeng/inputnumber';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { TableModule } from 'primeng/table';
import { LocalizationService } from '../../core/localization/localization.service';
import { JalaliDateInputComponent } from '../../shared/design-system/jalali-date-input.component';
import { InfoHintComponent } from '../../shared/design-system/info-hint.component';
import { AinStateComponent } from '../../shared/design-system/state.component';
import { bindInventoryLiveUpdates } from './inventory-live-updates';
import { CurrentContextService } from '../../core/context/current-context.service';
import { LiveUpdateService } from '../../core/live-updates/live-update.service';
import {
  CatalogRunningNumberKeys, CatalogService, CatalogWarehouse, CatalogWarehouseBin, InventoryLot, InventorySerial
} from './catalog.service';

@Component({
  standalone: true,
  selector: 'ain-offering-inventory-lots-panel',
  imports: [
    AinStateComponent, InfoHintComponent, FormsModule, TableModule, ButtonModule, DialogModule,
    InputTextModule, InputNumberModule, SelectModule, JalaliDateInputComponent
  ],
  template: `
    <ain-info-hint
      [titleKey]="'catalog.offerings.inventory.lots.hint.title'"
      [descriptionKey]="'catalog.offerings.inventory.lots.hint.description'" />

    @if (loading()) {
      <ain-state type="loading" [title]="t('catalog.loading')" />
    } @else {
      <div class="toolbar">
        <button pButton type="button" [label]="t('catalog.action.create')" (click)="openCreate()"></button>
        <button pButton type="button" severity="secondary" [label]="t('catalog.action.refresh')" (click)="load()"></button>
      </div>

      <p-table [value]="rows()" [paginator]="rows().length > 15" [rows]="15">
        <ng-template pTemplate="header">
          <tr>
            <th>{{ t('catalog.tracking.lotNumber') }}</th>
            <th>{{ t('columns.status') }}</th>
            <th>{{ t('catalog.inventory.quantity') }}</th>
            <th>{{ t('catalog.inventory.warehouseId') }}</th>
            <th>{{ t('catalog.tracking.expiryDate') }}</th>
            <th></th>
          </tr>
        </ng-template>
        <ng-template pTemplate="body" let-row>
          <tr>
            <td><button type="button" class="link-btn" (click)="openDetail(row)">{{ row.lotNumber }}</button></td>
            <td>{{ row.status }}</td>
            <td>{{ row.quantity }}</td>
            <td>{{ resolveWarehouse(row.warehouseId) }}</td>
            <td>{{ row.expiryDate || '—' }}</td>
            <td>
              <button pButton type="button" size="small" severity="secondary" [label]="t('common.view')" (click)="openDetail(row)"></button>
            </td>
          </tr>
        </ng-template>
      </p-table>

      @if (error()) {
        <p class="error">{{ error() }}</p>
      }
      @if (message()) {
        <p class="message">{{ message() }}</p>
      }
    }

    <p-dialog [(visible)]="createDialogVisible" [header]="t('catalog.tracking.lots.createTitle')" [modal]="true" [style]="{ width: '520px' }">
      <div class="form-grid">
        <label>
          <span>{{ t('catalog.tracking.lotNumber') }}</span>
          <input pInputText [(ngModel)]="createForm.lotNumber" [disabled]="isAutoLotNumber" />
          @if (isAutoLotNumber) {
            <button type="button" class="manual-link" (click)="enableManualLotNumber()">{{ t('catalog.tracking.enterManually') }}</button>
          }
        </label>
        <label>
          <span>{{ t('catalog.inventory.warehouseId') }}</span>
          <p-select [(ngModel)]="createForm.warehouseId" [options]="warehouseOptions()" optionLabel="label" optionValue="value" (ngModelChange)="loadBins($event)" appendTo="body" />
        </label>
        <label>
          <span>{{ t('catalog.inventory.binId') }}</span>
          <p-select [(ngModel)]="createForm.binId" [options]="binOptions()" optionLabel="label" optionValue="value" appendTo="body" />
        </label>
        <label>
          <span>{{ t('catalog.inventory.quantity') }}</span>
          <p-inputNumber [(ngModel)]="createForm.quantity" [min]="0" />
        </label>
        <label>
          <span>{{ t('catalog.tracking.manufacturedDate') }}</span>
          <ain-date-input [(ngModel)]="createForm.manufacturedDate" />
        </label>
        <label>
          <span>{{ t('catalog.tracking.expiryDate') }}</span>
          <ain-date-input [(ngModel)]="createForm.expiryDate" />
        </label>
        <label>
          <span>{{ t('catalog.tracking.sourceReference') }}</span>
          <input pInputText [(ngModel)]="createForm.sourceReferenceNumber" />
        </label>
      </div>
      <ng-template pTemplate="footer">
        <button pButton type="button" severity="secondary" [label]="t('common.cancel')" (click)="createDialogVisible = false"></button>
        <button pButton type="button" [label]="t('common.save')" [disabled]="saving()" (click)="saveCreate()"></button>
      </ng-template>
    </p-dialog>

    <p-dialog [(visible)]="detailDialogVisible" [header]="detailLot()?.lotNumber ?? ''" [modal]="true" [style]="{ width: 'min(640px, 96vw)' }">
      @if (detailLot(); as lot) {
        <div class="detail-actions">
          <button pButton type="button" severity="warn" [label]="t('catalog.tracking.action.quarantine')" (click)="updateStatus('Quarantine')"></button>
          <button pButton type="button" severity="secondary" [label]="t('catalog.tracking.action.release')" (click)="updateStatus('Release')"></button>
        </div>
        <div class="detail-grid">
          <div>{{ t('columns.status') }}: {{ lot.status }}</div>
          <div>{{ t('catalog.inventory.quantity') }}: {{ lot.quantity }}</div>
          <div>{{ t('catalog.inventory.warehouseId') }}: {{ resolveWarehouse(lot.warehouseId) }}</div>
        </div>
        @if (serials().length > 0) {
          <h4>{{ t('catalog.tracking.serials.tab') }}</h4>
          <p-table [value]="serials()" [paginator]="serials().length > 10" [rows]="10">
            <ng-template pTemplate="header">
              <tr>
                <th>{{ t('catalog.tracking.serialNumber') }}</th>
                <th>{{ t('catalog.tracking.status') }}</th>
              </tr>
            </ng-template>
            <ng-template pTemplate="body" let-serial>
              <tr>
                <td>{{ serial.serialNumber }}</td>
                <td>{{ serial.status }}</td>
              </tr>
            </ng-template>
          </p-table>
        }
      }
    </p-dialog>
  `,
  styles: [`
    .toolbar { display: flex; gap: 8px; margin-bottom: 10px; flex-wrap: wrap; }
    .link-btn { border: 0; background: none; color: #1d4ed8; cursor: pointer; font-weight: 600; padding: 0; }
    .error { color: #b91c1c; margin-top: 8px; }
    .message { color: #166534; margin-top: 8px; }
    .form-grid { display: grid; gap: 10px; }
    .form-grid label { display: grid; gap: 6px; }
    .manual-link { border: none; background: none; color: #2563eb; cursor: pointer; font-size: 12px; text-decoration: underline; justify-self: start; }
    .detail-actions { display: flex; gap: 8px; margin-bottom: 12px; flex-wrap: wrap; }
    .detail-grid { display: grid; gap: 6px; margin-bottom: 12px; font-size: 14px; }
    h4 { margin: 12px 0 8px; font-size: 14px; }
  `]
})
export class OfferingInventoryLotsPanelComponent implements OnInit {
  @Input({ required: true }) offeringId!: string;
  @Output() inventoryChanged = new EventEmitter<void>();

  private readonly service = inject(CatalogService);
  private readonly localization = inject(LocalizationService);
  private readonly contextService = inject(CurrentContextService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly liveUpdateService = inject(LiveUpdateService);

  readonly loading = signal(true);
  readonly saving = signal(false);
  readonly error = signal('');
  readonly message = signal('');
  readonly rows = signal<InventoryLot[]>([]);
  readonly warehouses = signal<CatalogWarehouse[]>([]);
  readonly bins = signal<CatalogWarehouseBin[]>([]);
  readonly serials = signal<InventorySerial[]>([]);
  readonly detailLot = signal<InventoryLot | null>(null);

  createDialogVisible = false;
  detailDialogVisible = false;
  isAutoLotNumber = true;

  createForm = {
    lotNumber: '',
    warehouseId: '',
    binId: '',
    quantity: 1,
    manufacturedDate: null as string | null,
    expiryDate: null as string | null,
    sourceReferenceNumber: ''
  };

  ngOnInit(): void {
    bindInventoryLiveUpdates(
      { liveUpdateService: this.liveUpdateService, contextService: this.contextService, destroyRef: this.destroyRef },
      () => this.load(false)
    );
    this.service.listWarehouses().subscribe({ next: w => this.warehouses.set(w) });
    this.load();
  }

  load(showLoading = true): void {
    if (showLoading) this.loading.set(true);
    this.service.listInventoryLots(this.offeringId).subscribe({
      next: rows => {
        this.rows.set(rows);
        this.loading.set(false);
      },
      error: () => {
        this.rows.set([]);
        this.error.set(this.t('catalog.errorLoad'));
        this.loading.set(false);
      }
    });
  }

  warehouseOptions(): Array<{ label: string; value: string }> {
    return this.warehouses().map(w => ({ label: `${w.code} - ${w.name}`, value: w.id }));
  }

  binOptions(): Array<{ label: string; value: string }> {
    return [
      { label: this.t('common.none'), value: '' },
      ...this.bins().map(b => ({ label: `${b.code} - ${b.name}`, value: b.id }))
    ];
  }

  resolveWarehouse(id: string): string {
    const w = this.warehouses().find(item => item.id === id);
    return w ? `${w.code} - ${w.name}` : id;
  }

  openCreate(): void {
    this.isAutoLotNumber = true;
    this.createForm = {
      lotNumber: '',
      warehouseId: this.warehouses()[0]?.id ?? '',
      binId: '',
      quantity: 1,
      manufacturedDate: null,
      expiryDate: null,
      sourceReferenceNumber: ''
    };
    if (this.createForm.warehouseId) {
      this.loadBins(this.createForm.warehouseId);
    }
    this.loadLotNumberPreview();
    this.createDialogVisible = true;
  }

  enableManualLotNumber(): void {
    this.isAutoLotNumber = false;
    this.createForm.lotNumber = '';
  }

  private loadLotNumberPreview(): void {
    if (!this.isAutoLotNumber) return;
    this.service.previewNextRunningNumber(
      CatalogRunningNumberKeys.inventoryLot,
      CatalogRunningNumberKeys.offeringParent,
      this.offeringId
    ).subscribe({
      next: preview => {
        if (preview && this.isAutoLotNumber) {
          this.createForm.lotNumber = preview;
        }
      }
    });
  }

  loadBins(warehouseId: string): void {
    if (!warehouseId) {
      this.bins.set([]);
      return;
    }
    this.service.listWarehouseBins(warehouseId).subscribe({ next: rows => this.bins.set(rows) });
  }

  saveCreate(): void {
    if ((!this.isAutoLotNumber && !this.createForm.lotNumber.trim()) || !this.createForm.warehouseId) {
      this.error.set(this.t('catalog.tracking.lots.requiredFields'));
      return;
    }
    this.saving.set(true);
    this.service.createInventoryLot({
      offeringId: this.offeringId,
      warehouseId: this.createForm.warehouseId,
      binId: this.createForm.binId || null,
      lotNumber: this.isAutoLotNumber ? '' : this.createForm.lotNumber.trim(),
      quantity: this.createForm.quantity,
      manufacturedDate: this.toDateOnly(this.createForm.manufacturedDate),
      expiryDate: this.toDateOnly(this.createForm.expiryDate),
      sourceReferenceNumber: this.createForm.sourceReferenceNumber.trim()
    }).subscribe({
      next: created => {
        this.saving.set(false);
        this.createDialogVisible = false;
        this.message.set(this.t('common.saved'));
        this.load();
        this.inventoryChanged.emit();
        this.openDetail(created);
      },
      error: () => {
        this.saving.set(false);
        this.error.set(this.t('catalog.tracking.lots.createFailed'));
      }
    });
  }

  openDetail(lot: InventoryLot): void {
    this.detailLot.set(lot);
    this.detailDialogVisible = true;
    this.service.listInventorySerials(undefined, lot.id).subscribe({ next: rows => this.serials.set(rows) });
  }

  updateStatus(status: string): void {
    const lot = this.detailLot();
    if (!lot) return;
    this.service.updateInventoryLotStatus(lot.id, status).subscribe({
      next: updated => {
        this.detailLot.set(updated);
        this.load(false);
        this.inventoryChanged.emit();
      },
      error: () => this.error.set(this.t('catalog.tracking.lots.updateFailed'))
    });
  }

  private toDateOnly(value: string | null): string | null {
    if (!value) return null;
    return value.slice(0, 10);
  }

  t(key: string): string {
    return this.localization.translate(key);
  }
}
